import { useState, useEffect, createContext, useContext, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/i18n/LangContext";
import { LangToggle } from "@/components/LangToggle";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import CompliancePage from "@/pages/CompliancePage";
import GdprConsent from "@/components/GdprConsent";
import CookieConsent from "@/components/CookieConsent";
import InstallPwaButton from "@/components/InstallPwaButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("pink_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setUser(res.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem("pink_token");
          setToken(null);
          setUser(null);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("pink_token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("pink_token");
    setToken(null);
    setUser(null);
  };

  const authValue = useMemo(
    () => ({ user, setUser, token, login, logout, loading }),
    [user, token, loading]
  );

  return (
    <AuthContext.Provider value={authValue}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#050505]" />;
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" />;
  return children;
}

const TOASTER_OPTIONS = {
  style: {
    background: "#0a0a0a",
    border: "1px solid rgba(217, 70, 239, 0.3)",
    color: "#e5e5e5",
  },
};

function App() {
  useEffect(() => {
    document.title = "Purple INK Studio";
    const hideBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) { badge.remove(); return; }
      requestAnimationFrame(hideBadge);
    };
    hideBadge();
  }, []);
  return (
    <div className="App">
      <LangProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <GdprConsent />
            <CookieConsent />
            <InstallPwaButton />
            <Toaster
              position="bottom-right"
              toastOptions={TOASTER_OPTIONS}
            />
            <LangToggle />
          </AuthProvider>
        </BrowserRouter>
      </LangProvider>
    </div>
  );
}

export default App;
